import type { ReactNode } from 'react';
import { cn } from '../lib/styles';

export type RichToggleOption<T extends string = string> = {
  value: T;
  label: string;
  /** Second line under the label, e.g. an option count or a connection status. */
  sublabel?: string;
  /** Sized by the caller, so a `sm` strip can carry a smaller glyph than an `md` one. */
  icon?: ReactNode;
  disabled?: boolean;
};

export type RichToggleProps<T extends string = string> = {
  value: T;
  options: ReadonlyArray<RichToggleOption<T>>;
  onChange: (value: T) => void;
  /** Accessible name for the group, where nothing visible already names it. */
  label?: string;
  /** `xs` is the inline strip, `sm` the compact one, `md` the control-height default. @default 'md' */
  size?: 'xs' | 'sm' | 'md';
  className?: string;
};

const FRAME = {
  xs: 'rounded',
  sm: 'rounded-md',
  md: 'rounded-lg border border-spark-border-default shadow-xs',
};

const OPTION = {
  xs: 'gap-0.5 rounded-sm px-1 py-px text-[11px]',
  sm: 'gap-1 rounded px-1.5 py-1 text-xs',
  md: 'gap-1.5 rounded-md px-3 py-1 text-sm',
};

const SELECTED = {
  xs: 'shadow-xs',
  sm: 'shadow-xs',
  md: 'shadow-sm',
};

export function RichToggle<T extends string = string>({
  value,
  options,
  onChange,
  label,
  size = 'md',
  className,
}: RichToggleProps<T>) {
  return (
    <fieldset className={cn('flex gap-0.5 bg-spark-surface-muted p-0.5', FRAME[size], className)}>
      {label && <legend className="sr-only">{label}</legend>}
      {options.map((option) => {
        const selected = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
            className={cn(
              'flex flex-1 items-center justify-center font-medium transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-spark-accent-hover',
              OPTION[size],
              option.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
              selected
                ? cn('bg-spark-surface text-spark-text-primary', SELECTED[size])
                : 'text-spark-text-secondary hover:text-spark-text-primary',
            )}
          >
            {option.icon}
            <div className="flex flex-col items-center">
              <span>{option.label}</span>
              {option.sublabel && (
                <span className="text-[10px] leading-tight opacity-60">{option.sublabel}</span>
              )}
            </div>
          </button>
        );
      })}
    </fieldset>
  );
}
